import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { ArrowLeft, Download, Send, CheckCircle, Clock } from 'lucide-react';

const UC_CONFIG = {
  uc1: { titre: "UC1: ENCADRER TOUT PUBLIC DANS TOUT LIEU ET TOUTE STRUCTURE", heures_standard: 48 },
  uc2: { titre: "UC2: METTRE EN OEUVRE UN PROJET D'ANIMATION S'INSCRIVANT DANS LE PROJET DE LA STRUCTURE", heures_standard: 64 },
  uc3: { titre: "UC3: CONDUIRE UNE SEANCE, UN CYCLE D'ANIMATION DANS LE CHAMP DU MOTONAUTISME", heures_standard: 184 },
  uc4: { titre: "UC4: MOBILISER LES TECHNIQUES DE LA MENTION MOTONAUTISME ET DISCIPLINES ASSOCIEES", heures_standard: 264 },
  foad: { titre: "FOAD (Formation Ouverte A Distance)", heures_standard: 40 }
};

const ALLEGEMENT_OPTIONS = [
  { value: 'aucun', label: 'Aucun allegement' },
  { value: 'autre_bpjeps', label: 'Allegement du a un autre BPJEPS ou diplome' },
  { value: 'parcours_pro', label: 'Allegement du au parcours professionnel' },
  { value: 'refus', label: "Refus d'allegement par le candidat" }
];

const makeDefaultUC = (heures) => ({
  heures_complete: heures,
  heures_allegee: 0,
  heures_renforcee: 0,
  allegement: 'aucun',
  vae: false,
  dispense: false
});

const makeDefaultFOAD = () => ({
  heures_complete: 40,
  heures_allegee: 0,
  heures_renforcee: 0,
  has_computer: true
});

const normalizeUC = (data, defaultHeures) => {
  if (!data) return makeDefaultUC(defaultHeures);
  return {
    heures_complete: data.heures_complete ?? data.heures ?? defaultHeures,
    heures_allegee: data.heures_allegee ?? 0,
    heures_renforcee: data.heures_renforcee ?? 0,
    allegement: data.allegement ?? 'aucun',
    vae: data.vae ?? data.orientation_vae ?? false,
    dispense: data.dispense ?? data.dispense_certification ?? false
  };
};

const normalizeFOAD = (data) => {
  if (!data) return makeDefaultFOAD();
  return {
    heures_complete: data.heures_complete ?? data.heures ?? 40,
    heures_allegee: data.heures_allegee ?? 0,
    heures_renforcee: data.heures_renforcee ?? 0,
    has_computer: data.has_computer ?? data.dispose_ordinateur ?? true
  };
};

const calcTotals = (data) => {
  const keys = ['uc1', 'uc2', 'uc3', 'uc4', 'foad'];
  const totalAllegements = keys.reduce((s, k) => s + (data[k]?.heures_allegee || 0), 0);
  const totalComplete = keys.reduce((s, k) => s + (data[k]?.heures_complete || 0), 0);
  return {
    total_allegements: totalAllegements,
    total_heures_formation: totalComplete - totalAllegements
  };
};

const PIFEditor = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const candidatId = searchParams.get('id');

  const [candidat, setCandidat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [existingPif, setExistingPif] = useState(null);

  const [pifData, setPifData] = useState(() => {
    const init = {
      uc1: makeDefaultUC(48),
      uc2: makeDefaultUC(64),
      uc3: makeDefaultUC(184),
      uc4: makeDefaultUC(264),
      foad: makeDefaultFOAD(),
      total_heures_stage: 496,
      total_allegements: 0,
      total_heures_formation: 600
    };
    return init;
  });

  useEffect(() => {
    if (candidatId) loadData();
  }, [candidatId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const candidatRes = await axios.get(`/candidats/${candidatId}`);
      setCandidat(candidatRes.data);

      try {
        const pifRes = await axios.get(`/candidats/${candidatId}/pif`);
        if (pifRes.data) {
          setExistingPif(pifRes.data);
          const d = pifRes.data;
          const newData = {
            uc1: normalizeUC(d.uc1 || d.bc1, 48),
            uc2: normalizeUC(d.uc2 || d.bc2, 64),
            uc3: normalizeUC(d.uc3 || d.bc3, 184),
            uc4: normalizeUC(d.uc4, 264),
            foad: normalizeFOAD(d.foad),
            total_heures_stage: d.total_heures_stage || 496
          };
          const totals = calcTotals(newData);
          setPifData({ ...newData, ...totals });
        }
      } catch (e) {
        // No existing PIF
      }
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const updateUC = useCallback((ucKey, field, value) => {
    setPifData(prev => {
      const newData = {
        ...prev,
        [ucKey]: { ...prev[ucKey], [field]: value }
      };
      const totals = calcTotals(newData);
      return { ...newData, ...totals };
    });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`/candidats/${candidatId}/pif`, pifData);
      toast.success('PIF enregistre avec succes !');
      navigate(-1);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await axios.get(`/candidats/${candidatId}/pif/pdf`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${candidat?.nom || ''}_${candidat?.prenom || ''}_PIF.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erreur lors du telechargement du PDF');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#8DC63F]"></div>
      </div>
    );
  }

  const renderUCSection = (ucKey, config) => {
    const ucData = pifData[ucKey];
    const isFoad = ucKey === 'foad';

    return (
      <Card key={ucKey} className="mb-4" data-testid={`pif-section-${ucKey}`}>
        <CardHeader className={`py-3 ${isFoad ? 'bg-blue-500/10' : 'bg-[#8DC63F]/10'}`}>
          <CardTitle className="text-sm font-bold text-[#0F172A]">
            {config.titre}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Heures de formation */}
            <div className="space-y-2">
              <Label className="text-xs font-medium text-slate-500">Heures de formation</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm w-28 text-green-700">Complete</span>
                <input
                  data-testid={`${ucKey}-heures-complete`}
                  type="number"
                  min="0"
                  className="w-20 h-8 px-2 border rounded-md text-sm text-center"
                  value={ucData.heures_complete}
                  onChange={(e) => {
                    const val = e.target.value === '' ? 0 : parseInt(e.target.value, 10);
                    if (!isNaN(val) && val >= 0) updateUC(ucKey, 'heures_complete', val);
                  }}
                />
                <span className="text-xs text-slate-400">h</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm w-28 text-amber-600">Allegee (-)</span>
                <input
                  data-testid={`${ucKey}-heures-allegee`}
                  type="number"
                  min="0"
                  className="w-20 h-8 px-2 border rounded-md text-sm text-center"
                  value={ucData.heures_allegee}
                  onChange={(e) => {
                    const val = e.target.value === '' ? 0 : parseInt(e.target.value, 10);
                    if (!isNaN(val) && val >= 0) updateUC(ucKey, 'heures_allegee', val);
                  }}
                />
                <span className="text-xs text-slate-400">h</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm w-28 text-blue-600">Renforcee (+)</span>
                <input
                  data-testid={`${ucKey}-heures-renforcee`}
                  type="number"
                  min="0"
                  className="w-20 h-8 px-2 border rounded-md text-sm text-center"
                  value={ucData.heures_renforcee}
                  onChange={(e) => {
                    const val = e.target.value === '' ? 0 : parseInt(e.target.value, 10);
                    if (!isNaN(val) && val >= 0) updateUC(ucKey, 'heures_renforcee', val);
                  }}
                />
                <span className="text-xs text-slate-400">h</span>
              </div>
            </div>

            {/* Observations / Allegements (non-FOAD) */}
            {!isFoad && (
              <div>
                <Label className="text-xs font-medium text-slate-500">Observations / Allegements</Label>
                <select
                  data-testid={`${ucKey}-allegement`}
                  value={ucData.allegement}
                  onChange={(e) => updateUC(ucKey, 'allegement', e.target.value)}
                  className="mt-2 w-full h-9 px-3 border rounded-md text-sm"
                >
                  {ALLEGEMENT_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <div className="mt-3 space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={ucData.vae}
                      onChange={(e) => updateUC(ucKey, 'vae', e.target.checked)}
                      className="rounded text-[#8DC63F]"
                    />
                    <span className="text-xs">Orientation VAE</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={ucData.dispense}
                      onChange={(e) => updateUC(ucKey, 'dispense', e.target.checked)}
                      className="rounded text-[#8DC63F]"
                    />
                    <span className="text-xs">Dispense de certification</span>
                  </label>
                </div>
              </div>
            )}

            {/* FOAD specifique */}
            {isFoad && (
              <div>
                <Label className="text-xs font-medium text-slate-500">Prerequis</Label>
                <label className="flex items-center gap-2 cursor-pointer mt-2">
                  <input
                    data-testid="foad-has-computer"
                    type="checkbox"
                    checked={ucData.has_computer}
                    onChange={(e) => updateUC(ucKey, 'has_computer', e.target.checked)}
                    className="rounded text-[#8DC63F]"
                  />
                  <span className="text-xs">Dispose d'un ordinateur/tablette + internet</span>
                </label>
              </div>
            )}

            {/* Formateur */}
            <div>
              <Label className="text-xs font-medium text-slate-500">Formateur(s)</Label>
              <p className="mt-2 text-sm text-slate-400 italic">(a definir)</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const totalComplete = ['uc1', 'uc2', 'uc3', 'uc4', 'foad'].reduce((s, k) => s + (pifData[k]?.heures_complete || 0), 0);

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-3">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(-1)} data-testid="pif-back-btn">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-[#0F172A]">Plan Individuel de Formation</h1>
              {candidat && (
                <p className="text-slate-500">{candidat.prenom} {candidat.nom}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {existingPif && (
              <Button variant="outline" onClick={handleDownloadPDF} data-testid="pif-download-pdf">
                <Download className="h-4 w-4 mr-2" />
                PDF
              </Button>
            )}
            <Button onClick={handleSave} disabled={saving} className="bg-[#8DC63F] hover:bg-[#7db336]" data-testid="pif-save-btn">
              <Send className="h-4 w-4 mr-2" />
              {saving ? 'Enregistrement...' : existingPif ? 'Mettre a jour' : 'Enregistrer et envoyer'}
            </Button>
          </div>
        </div>

        {/* Existing status */}
        {existingPif && (
          <Card className={`mb-6 ${existingPif.statut === 'signe' ? 'border-green-500' : 'border-amber-500'}`}>
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                {existingPif.statut === 'signe' ? (
                  <>
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <div>
                      <p className="font-medium text-green-700">PIF signe par le candidat</p>
                      <p className="text-sm text-slate-500">Signe le {existingPif.date_signature}</p>
                    </div>
                  </>
                ) : (
                  <>
                    <Clock className="h-5 w-5 text-amber-500" />
                    <div>
                      <p className="font-medium text-amber-700">En attente de signature du candidat</p>
                      <p className="text-sm text-slate-500">Envoye le {existingPif.created_at ? new Date(existingPif.created_at).toLocaleDateString('fr-FR') : ''}</p>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* UC Sections */}
        {Object.entries(UC_CONFIG).map(([key, config]) => renderUCSection(key, config))}

        {/* Recap */}
        <Card className="border-2 border-[#8DC63F]" data-testid="pif-recap">
          <CardHeader className="bg-[#8DC63F] text-white py-3">
            <CardTitle className="text-sm font-bold">RECAPITULATIF</CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div>
                <p className="text-xs text-slate-500">Heures de stage</p>
                <p className="text-xl font-bold text-[#0F172A]" data-testid="pif-total-stage">{pifData.total_heures_stage}h</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Formation totale</p>
                <p className="text-xl font-bold text-[#0F172A]" data-testid="pif-total-formation-base">{totalComplete}h</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Allegements</p>
                <p className="text-xl font-bold text-amber-600" data-testid="pif-total-allegements">-{pifData.total_allegements}h</p>
              </div>
              <div className="bg-[#8DC63F]/10 rounded-lg p-2">
                <p className="text-xs text-slate-500">Total candidat</p>
                <p className="text-xl font-bold text-[#8DC63F]" data-testid="pif-total-candidat">{pifData.total_heures_formation}h</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PIFEditor;
